import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import printTemplateService from '../../../services/printTemplateService';
import PrintTemplateEditor from '../../../features/print-template-editor/PrintTemplateEditor';
import SplashScreen from '../../../features/print-template-editor/ui/SplashScreen';
import { createEmptyDocument } from '../../../features/print-template-editor/schema/documentSchema';
import { exportCanvasZip, exportCanvasThumbnail } from '../../../features/print-template-editor/export/exportCanvas';
import { usePrintEditorStore } from '../../../features/print-template-editor/state/usePrintEditorStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import { useIncomeExpenseStore } from '../../../store/incomeExpenseStore';
import { moduleAutoFill } from '../../../lib/incomeExpenseDefaults';
import LimitReachedModal from '../../../components/common/LimitReachedModal';

function parseCanvasDocument(raw, fallbackWidth, fallbackHeight) {
    let doc = raw;
    if (typeof doc === 'string') {
        try {
            doc = JSON.parse(doc);
        } catch {
            doc = null;
        }
    }
    if (!doc || typeof doc !== 'object') {
        return createEmptyDocument({
            width: fallbackWidth || 1050,
            height: fallbackHeight || 600,
        });
    }
    const canvas = { ...(doc.canvas || {}) };
    if (!canvas.width && fallbackWidth) canvas.width = fallbackWidth;
    if (!canvas.height && fallbackHeight) canvas.height = fallbackHeight;
    return { ...doc, canvas };
}

export default function PrintMediaEditor() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const user = useAuthStore((s) => s.user);
    const getCurrentUser = useAuthStore((s) => s.getCurrentUser);
    const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
    const isOperator = user?.role === 'OPERATOR' || user?.role === 'operator';

    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [template, setTemplate] = useState(null);
    const [initialDoc, setInitialDoc] = useState(null);
    const [loadKey, setLoadKey] = useState(0);
    const [limitModalOpen, setLimitModalOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setInitialDoc(null);
        (async () => {
            try {
                const res = await printTemplateService.get(templateId);
                const tpl = res?.data;
                if (!tpl || cancelled) return;
                setTemplate(tpl);
                setInitialDoc(parseCanvasDocument(tpl.canvasJson, tpl.width, tpl.height));
                setLoadKey((k) => k + 1);
            } catch (err) {
                notifications.show({ title: 'Error', message: err.message || 'Template not found', color: 'red' });
                navigate('/print-media');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [templateId, navigate]);

    const headerLeft = useMemo(() => (
        <div className="flex items-center gap-0.5 min-w-0">
            <button
                type="button"
                title="Back to print media"
                onClick={() => navigate('/print-media')}
                className="h-7 w-7 shrink-0 flex items-center justify-center rounded-[6px] text-black/60 dark:text-white/60 hover:bg-[#f5f5f5] dark:hover:bg-[#383838] transition-colors"
            >
                <IconArrowLeft size={15} />
            </button>
            <span className="text-[13px] font-semibold text-black dark:text-white truncate px-1">
                {template?.title || 'Print template'}
            </span>
        </div>
    ), [template, navigate]);

    const handleExport = async () => {
        if (exporting) return;
        if (!selectedOfficeSpace?.id) {
            notifications.show({
                title: 'Office space required',
                message: 'Select an office space before exporting print media.',
                color: 'orange',
            });
            return;
        }
        setExporting(true);
        try {
            const exportId = template?.id || templateId;
            // Read-only check first (refuses an empty balance up front) — the credit is taken only AFTER the ZIP was made.
            await printTemplateService.authorizeExport(exportId, {
                officeSpaceId: selectedOfficeSpace.id,
                format: 'zip',
                checkOnly: true,
            });
            const doc = usePrintEditorStore.getState().document;
            const { pngBlob, pdfBlob, width, height, pages } = await exportCanvasZip({
                download: true,
                filenameBase: template?.slug || 'print',
                scale: 2,
                document: doc,
            });
            await printTemplateService.authorizeExport(exportId, {
                officeSpaceId: selectedOfficeSpace.id,
                format: 'zip',
            });
            try {
                await getCurrentUser();
            } catch {
                // balance refresh optional
            }
            // The work has left the building — the ZIP is downloaded and the copy
            // is on its way to Documents, so stop warning about losing it.
            usePrintEditorStore.getState().markClean();
            notifications.show({
                title: 'Exported',
                // A multi-frame template downloads one PNG per frame plus a single
                // PDF with a page each — say so, or the extra files look like a bug.
                message: pages?.length > 1
                    ? `ZIP downloaded (${pages.length} PNGs + ${pages.length}-page PDF, print credit used)`
                    : 'ZIP downloaded (PNG + PDF, print credit used)',
                color: 'green',
            });

            // Persist it so it appears under Documents → Print Media. Best-effort:
            // the user already has their download and paid the credit, so a storage
            // failure must not surface as a failed export.
            try {
                const thumbnail = await exportCanvasThumbnail({ maxWidth: 480, document: doc });
                await printTemplateService.saveGenerated({
                    officeSpaceId: selectedOfficeSpace.id,
                    templateId: template?.id || templateId,
                    title: template?.title || 'Print media',
                    pngBlob,
                    pdfBlob,
                    thumbnail,
                    width,
                    height,
                    pages,
                });
            } catch (saveErr) {
                console.warn('[print-media] Could not save the export to your documents:', saveErr?.message || saveErr);
            }

            if (user?.settings?.autoAppearIncomeExpense === true) {
                const fill = moduleAutoFill(user?.settings, 'print_media');
                openIncomeExpenseModal({
                    name: fill.name,
                    type: 'income',
                    amount: fill.price,
                    category: 'print_media',
                    referenceId: template?.id,
                });
            }
        } catch (err) {
            if (err?.code === 'INSUFFICIENT_CREDITS') {
                // Backend rejects the export when the balance is below the print cost.
                // Toast the shortfall (server sends required/available), then offer top-up.
                const { required, available } = err.data || {};
                notifications.show({
                    title: t('printMedia.lowCreditTitle'),
                    message: (required != null && available != null)
                        ? t('errors.INSUFFICIENT_CREDITS', { required, available })
                        : (err.message || t('printMedia.lowCreditFallback')),
                    color: 'orange',
                });
                setLimitModalOpen(true);
                return;
            }
            notifications.show({ title: 'Error', message: err.message || 'Export failed', color: 'red' });
        } finally {
            setExporting(false);
        }
    };

    if (loading || !initialDoc) {
        return <SplashScreen persistent />;
    }

    return (
        <div className="h-dvh w-full">
            <PrintTemplateEditor
                key={`user-editor-${templateId}-${loadKey}`}
                initialDocument={initialDoc}
                mode="user"
                onSave={handleExport}
                onExport={handleExport}
                saving={exporting}
                saveLabel={exporting ? 'Exporting…' : 'Download'}
                // Nothing is stored on this side — edits exist only until they are
                // downloaded, so the warning says that rather than "unsaved".
                unsavedMessage="Your edits have not been downloaded yet. Leave this page and lose them?"
                headerLeft={headerLeft}
            />
            <LimitReachedModal
                opened={limitModalOpen}
                onClose={() => setLimitModalOpen(false)}
                isOperator={isOperator}
            />
        </div>
    );
}
